import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from 'wouter';
import { useAuth } from '@/contexts/auth-context';
import StatsOverview from '@/components/stats-overview';
import VideoList from '@/components/video-list';
import ImportModal from '@/components/import-modal';

import { VideoData, CategoryData, getUserVideos, getUserCategories, deleteVideo, updateVideo, timestampToString } from '@/lib/firebase';
import { Video, Plus, Settings, LogOut } from 'lucide-react';

export default function Dashboard() {
  const [videos, setVideos] = useState<VideoData[]>([]);
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const [location, setLocation] = useLocation();

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const [videosData, categoriesData] = await Promise.all([
        getUserVideos(user.uid),
        getUserCategories(user.uid),
      ]);
      
      setVideos(videosData);
      setCategories(categoriesData);
    } catch (error: any) {
      toast({
        title: "Failed to load data",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditVideo = (video: VideoData) => {
    // Preserve current URL parameters when navigating to edit
    const currentParams = new URLSearchParams(window.location.search);
    const returnParams = currentParams.toString();
    setLocation(`/videos/edit?id=${video.id}${returnParams ? '&return=' + encodeURIComponent('?' + returnParams) : ''}`);
  };

  const handleDeleteVideo = async (videoId: string) => {
    const video = videos.find(v => v.id === videoId);
    if (!video) return;
    
    if (!confirm(`Are you sure you want to disable "${video.title}"? This will remove it from the list but not permanently delete it.`)) {
      return;
    }
    
    try {
      await updateVideo(videoId, { enabled: false });
      setVideos(videos.filter(v => v.id !== videoId));
      
      toast({
        title: "Video disabled",
        description: `"${video.title}" has been disabled and removed from your library.`,
      });
    } catch (error: any) {
      toast({
        title: "Failed to disable video",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Signed out successfully",
        description: "See you next time!",
      });
    } catch (error: any) {
      toast({
        title: "Failed to sign out",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getEffectiveStatus = (video: VideoData) => {
    if (video.status === 'Published' && video.publishDate) {
      // Handle both string and timestamp formats
      const publishDateString = typeof video.publishDate === 'string' ? video.publishDate : timestampToString(video.publishDate);
      const publishDate = new Date(publishDateString);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      publishDate.setHours(0, 0, 0, 0);
      
      if (publishDate > today) {
        return 'Scheduled';
      }
    }
    return video.status;
  };

  const getStats = () => {
    const draft = videos.filter(v => getEffectiveStatus(v) === 'Draft').length;
    const scheduled = videos.filter(v => getEffectiveStatus(v) === 'Scheduled').length;
    const published = videos.filter(v => getEffectiveStatus(v) === 'Published').length;
    const blocked = videos.filter(v => getEffectiveStatus(v) === 'Blocked').length;
    
    return {
      totalVideos: videos.length,
      draft,
      scheduled,
      published,
      blocked,
    };
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-600">Loading your content...</p>
          </div>
        </div>
      </div>
    );
  }

  const stats = getStats();

  return (
    <div className="flex-1 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Videos</h1>
            <p className="text-slate-600">Manage your video content library</p>
          </div>
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => setLocation('/import')}
              className="bg-primary text-white hover:bg-primary/90 flex items-center space-x-2"
            >
              <Plus size={16} />
              <span>Process Videos</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setLocation('/categories')}
              className="flex items-center space-x-2"
            >
              <Settings size={16} />
              <span>Categories</span>
            </Button>
          </div>
        </div>
        {/* Stats Overview */}
        <StatsOverview 
          totalVideos={stats.totalVideos}
          imported={stats.draft}
          scheduled={stats.scheduled}
          published={stats.published}
          blocked={stats.blocked}
        />

        {/* Video List */}
        <VideoList
          videos={videos}
          categories={categories}
          onEditVideo={handleEditVideo}
          onDeleteVideo={handleDeleteVideo}
        />
      </div>

      {/* Modals */}
      <ImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        categories={categories}
        onVideosSaved={loadData}
        onCategoriesUpdated={loadData}
      />
    </div>
  );
}
